import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, formatAWG, usdApprox } from "@/lib/api";
import { useSalesTax } from "@/lib/tax";
import { BrandedLoader } from "@/components/util/BrandedLoader";
import { useCart } from "@/contexts/CartContext";
import { ArrowRight, Check, Star, Sparkles, X, MessageCircle } from "lucide-react";
import { toast } from "sonner";

// Display-only rename: internal tier key stays "standard" (or whatever the
// admin set) so existing orders keep pointing at the right record; only the
// visible label is switched. If Calvin renames the tier in the admin, this
// mapping is bypassed automatically because Calvin's chosen name won't match
// the case-insensitive "standard" trigger. The replacement label itself is
// now CMS-managed via `setup_essential_tier_label` (Phase B).
const displayTierName = (name, essentialLabel) =>
  (name || "").trim().toLowerCase() === "standard" ? (essentialLabel || "Essential") : name;

// Fallbacks live here as a safety net if `/settings` fails to load — they
// match the DEFAULT_SETTINGS on the backend so a broken settings call still
// renders sensible copy.
const CFG_FALLBACK = {
  intro: "Choose the performance level and extras that fit your workflow. We'll professionally configure and test everything before delivery.",
  back_link: "← All Workstations",
  step_platform_heading: "Choose Your Platform",
  step_platform_sub: "Select the platform that best fits your workflow. Available options and software may vary by workstation.",
  platform_windows_label: "Windows",
  platform_mac_label: "Mac",
  platform_recommended_badge: "Recommended",
  platform_current_model_label: "Current model",
  platform_current_model_disclaimer: "Model may vary based on availability. Performance specifications are guaranteed.",
  compat_guide_link_label: "Not sure which platform you need? View compatibility guide",
  compat_guide_modal_title: "Platform Compatibility Guide",
  compat_guide_empty_state: "Compatibility notes for this workstation haven't been added yet. Reach out on WhatsApp and we'll walk you through the platform trade-offs for your workflow.",
  whatsapp_link_label: "Still not sure? Chat with us on WhatsApp",
  whatsapp_url: "",
  step1_heading: "Choose Your Performance",
  step1_sub: "Pick the base configuration that fits your workload.",
  essential_label: "Essential",
  step2_heading: "Complete Your Setup",
  step2_sub: "Extras to level up your setup — pick as many as you need.",
  step3_heading: "Your Details",
  step3_sub: "A few essentials so we can build and deliver your setup.",
  delivery_note: "We'll configure, test, and ship to your address in Aruba. We'll confirm your exact delivery timeline after you submit your order.",
  submit_label: "Request This Setup",
  sentoo: "We'll confirm build & delivery details before anything is charged. Online payment isn't live yet — we'll follow up directly.",
};

// Only platforms marked `.active` show up as customer-selectable cards.
// Inactive combinations are hidden entirely (not rendered as broken/dead
// options) per the spec.
const PLATFORM_KEYS = ["windows", "mac"];

export default function SetupConfiguratorPage() {
  const { slug } = useParams();
  const { addItem } = useCart();
  const navigate = useNavigate();
  const [setup, setSetup] = useState(null);
  const [platformKey, setPlatformKey] = useState(null);
  const [tierId, setTierId] = useState(null);
  const [selectedAddons, setSelectedAddons] = useState(new Set());
  const [cfg, setCfg] = useState(CFG_FALLBACK);
  const [compatModalOpen, setCompatModalOpen] = useState(false);
  const [contact, setContact] = useState({
    name: "",
    email: "",
    phone: "",
    business: "",
    delivery_address: "",
    notes: "",
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get(`/laptops/${slug}`).then((r) => setSetup(r.data));
  }, [slug]);

  // Fetch CMS-managed configurator copy. Every visible string on this page
  // flows through this fetch — no hardcoded customer-facing text.
  useEffect(() => {
    api.get("/settings").then((r) => {
      const d = r.data || {};
      setCfg({
        intro: d.setup_intro_copy || CFG_FALLBACK.intro,
        back_link: d.setup_back_link_label || CFG_FALLBACK.back_link,
        step_platform_heading: d.setup_step_platform_heading || CFG_FALLBACK.step_platform_heading,
        step_platform_sub: d.setup_step_platform_sub || CFG_FALLBACK.step_platform_sub,
        platform_windows_label: d.setup_platform_windows_label || CFG_FALLBACK.platform_windows_label,
        platform_mac_label: d.setup_platform_mac_label || CFG_FALLBACK.platform_mac_label,
        platform_recommended_badge: d.setup_platform_recommended_badge || CFG_FALLBACK.platform_recommended_badge,
        platform_current_model_label: d.setup_platform_current_model_label || CFG_FALLBACK.platform_current_model_label,
        platform_current_model_disclaimer: d.setup_platform_current_model_disclaimer || CFG_FALLBACK.platform_current_model_disclaimer,
        compat_guide_link_label: d.setup_compat_guide_link_label || CFG_FALLBACK.compat_guide_link_label,
        compat_guide_modal_title: d.setup_compat_guide_modal_title || CFG_FALLBACK.compat_guide_modal_title,
        compat_guide_empty_state: d.setup_compat_guide_empty_state || CFG_FALLBACK.compat_guide_empty_state,
        whatsapp_link_label: d.setup_whatsapp_link_label ?? CFG_FALLBACK.whatsapp_link_label,
        whatsapp_url: d.cta_whatsapp_url || CFG_FALLBACK.whatsapp_url,
        step1_heading: d.setup_step1_heading || CFG_FALLBACK.step1_heading,
        step1_sub: d.setup_step1_sub || CFG_FALLBACK.step1_sub,
        essential_label: d.setup_essential_tier_label || CFG_FALLBACK.essential_label,
        step2_heading: d.setup_step2_heading || CFG_FALLBACK.step2_heading,
        step2_sub: d.setup_step2_sub || CFG_FALLBACK.step2_sub,
        step3_heading: d.setup_step3_heading || CFG_FALLBACK.step3_heading,
        step3_sub: d.setup_step3_sub || CFG_FALLBACK.step3_sub,
        delivery_note: d.setup_delivery_note || CFG_FALLBACK.delivery_note,
        submit_label: d.setup_submit_label || CFG_FALLBACK.submit_label,
        sentoo: d.setup_sentoo_disclaimer || CFG_FALLBACK.sentoo,
      });
    }).catch(() => {});
  }, []);

  // Which platforms are actually purchasable for this workstation. Only
  // renders cards Calvin has explicitly activated in the admin — inactive
  // combinations are dropped from the array entirely so no dead options
  // appear.
  const activePlatforms = useMemo(() => {
    const platforms = setup?.platforms || {};
    return PLATFORM_KEYS
      .map((key) => ({ key, ...(platforms[key] || {}) }))
      .filter((p) => p.active && Array.isArray(p.tiers) && p.tiers.length > 0);
  }, [setup]);

  // Default-select the selected platform ONLY when there's a single active
  // platform (no ambiguity possible). Otherwise leave it unpicked so the
  // customer has to make an explicit choice — matches the "no auto-advance"
  // spec (and forces Step 1 → Step 2 progression to happen consciously).
  useEffect(() => {
    if (!setup) return;
    if (activePlatforms.length === 1 && !platformKey) {
      setPlatformKey(activePlatforms[0].key);
    }
    // Preserve platform state across live-reloads of the CMS during dev.
  }, [setup, activePlatforms, platformKey]);

  // When the platform changes, snap the tier selection to the popular one
  // (or first) of the newly-selected platform. Tiers are per-platform, so
  // stale tier IDs from the other platform would silently invalidate the
  // Step 3 summary.
  const currentPlatform = useMemo(
    () => activePlatforms.find((p) => p.key === platformKey) || null,
    [activePlatforms, platformKey]
  );
  const tiers = currentPlatform?.tiers || [];
  useEffect(() => {
    if (!currentPlatform) {
      setTierId(null);
      return;
    }
    const popular = tiers.find((t) => t.popular) || tiers[0];
    setTierId(popular?.id || null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [platformKey, setup?.slug]);

  const tier = useMemo(() => tiers.find((t) => t.id === tierId), [tiers, tierId]);
  const addonItems = useMemo(
    () => (setup?.addons || []).filter((a) => selectedAddons.has(a.id)),
    [setup, selectedAddons]
  );
  const total = useMemo(() => {
    const base = tier?.price || 0;
    const ads = addonItems.reduce((s, a) => s + a.price, 0);
    return base + ads;
  }, [tier, addonItems]);
  // Site-wide tax — shared source with Checkout via /api/settings.
  // Display-only; backend re-validates order totals on submit.
  const { taxRate, taxLabel, compute: computeTax } = useSalesTax();
  const { taxAmount, total: totalWithTax } = computeTax(total);

  const toggleAddon = (id) => {
    const next = new Set(selectedAddons);
    next.has(id) ? next.delete(id) : next.add(id);
    setSelectedAddons(next);
  };

  const contactValid = (() => {
    if (!contact.name.trim() || !contact.email.trim() || !contact.phone.trim()) return false;
    if (!contact.delivery_address.trim()) return false;
    return true;
  })();

  const canSubmit = !!setup && !!currentPlatform && !!tier && contactValid && !submitting;

  const platformDisplayName = (key) =>
    key === "windows" ? cfg.platform_windows_label : cfg.platform_mac_label;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const platformName = platformDisplayName(currentPlatform.key);
      addItem({
        kind: "laptop",
        name: `${setup.name}${tier ? ` — ${platformName} · ${displayTierName(tier.name, cfg.essential_label)}` : ""}`,
        price: total,
        service_slug: setup.slug,
        line_type: "laptop_tier",
        item_code: tier?.item_code || "",
        item_id: tier?.id || "",
        quantity: 1,
        selected_addons: addonItems.map((a) => ({
          item_code: a.item_code || "",
          item_id: a.id,
          quantity: 1,
        })),
        meta: {
          slug: setup.slug,
          persona: setup.persona,
          platform: currentPlatform.key,
          platform_display: platformName,
          tier: tier?.name,
          tier_display: displayTierName(tier?.name, cfg.essential_label),
          current_model: tier?.current_model || "",
          addons: addonItems.map((a) => a.name),
          contact: { ...contact },
        },
      });
      toast.success("Setup request submitted — we'll follow up shortly.");
      navigate("/checkout");
    } finally {
      setSubmitting(false);
    }
  };

  if (!setup) {
    return <BrandedLoader testid="setup-loading" />;
  }

  const noPlatformActive = activePlatforms.length === 0;

  return (
    <div data-testid={`setup-configurator-${setup.slug}`} className="bg-[#0F0F0F]">
      {/* SLIM HEADER — see Phase 8.23 notes; the big image is retained. */}
      <section className="relative overflow-hidden grain border-b border-[#1F1F1F]">
        <div
          aria-hidden
          className="absolute -top-32 right-0 w-[600px] h-[600px] rounded-full opacity-25 blur-[140px]"
          style={{ background: "radial-gradient(circle, #FF6600 0%, transparent 60%)" }}
        />
        <div className="relative max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 py-14 grid lg:grid-cols-[1.1fr_1fr] gap-10 items-center">
          <div>
            <Link
              to="/workstations"
              className="ur-eyebrow inline-block hover:text-white transition-colors"
              data-testid="back-to-workstations"
            >
              {cfg.back_link}
            </Link>
            <div className="mt-4 flex items-center gap-3">
              {setup.icon_url && (
                <img
                  src={setup.icon_url}
                  alt=""
                  className="w-10 h-10 object-contain shrink-0"
                  style={{ filter: "drop-shadow(0 0 8px rgba(255,102,0,0.35))" }}
                />
              )}
              <h1
                data-testid="configurator-title"
                className="font-display text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight leading-[1.02]"
              >
                Configure Your <span className="text-[#FF6600]">{setup.name}</span>
              </h1>
            </div>
            <p
              data-testid="configurator-intro"
              className="mt-5 text-[#B5B5B5] text-base sm:text-lg max-w-xl leading-relaxed"
            >
              {cfg.intro}
            </p>
          </div>
          <div className="relative">
            <div className="aspect-[5/4] rounded-2xl overflow-hidden border border-[#1F1F1F] bg-gradient-to-br from-[#141414] to-[#0A0A0A] grid place-items-center p-8">
              <img
                src={setup.image}
                alt={setup.name}
                className="max-w-full max-h-full object-contain"
                data-testid="configurator-hero-image"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CONFIGURATOR */}
      <section className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 py-20 grid lg:grid-cols-[1fr_360px] gap-10 items-start">
        <div className="space-y-14">
          {/* Step 1 — Choose Your Platform (Phase 8.24). Renders ONLY the
              platform cards Calvin has activated in the admin — inactive
              combinations are dropped, not shown as disabled/broken options. */}
          {noPlatformActive ? (
            <div data-testid="no-platform-active" className="ur-card p-6 text-sm text-[#A3A3A3]">
              This workstation isn't currently available online. Please{" "}
              <Link to="/contact" className="text-[#FF6600] underline">contact us</Link>{" "}
              and we'll get you set up directly.
            </div>
          ) : (
            <div>
              <StepHeader
                n={1}
                title={cfg.step_platform_heading}
                sub={cfg.step_platform_sub}
              />
              <div className="mt-8 grid md:grid-cols-2 gap-5">
                {activePlatforms.map((p) => {
                  const active = p.key === platformKey;
                  const label = platformDisplayName(p.key);
                  return (
                    <button
                      key={p.key}
                      data-testid={`setup-platform-${p.key}`}
                      onClick={() => setPlatformKey(p.key)}
                      className={`text-left ur-card p-6 transition-all ${
                        active ? "border-[#FF6600] ring-2 ring-[#FF6600]/30" : "hover:border-[#FF6600]/40"
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="font-display text-2xl font-bold">{label}</div>
                        {p.badge && (
                          <span
                            data-testid={`setup-platform-${p.key}-badge`}
                            className="text-[10px] font-bold uppercase tracking-widest bg-[#FF6600] text-black px-2 py-1 rounded-full flex items-center gap-1"
                          >
                            <Star className="w-3 h-3" />
                            {p.badge === "Recommended" ? cfg.platform_recommended_badge : p.badge}
                          </span>
                        )}
                      </div>
                      {p.subtext && (
                        <div
                          data-testid={`setup-platform-${p.key}-subtext`}
                          className="text-xs text-[#A3A3A3] mt-3 leading-relaxed"
                        >
                          {p.subtext}
                        </div>
                      )}
                      <div
                        className={`mt-5 inline-flex items-center gap-2 text-xs font-bold ${
                          active ? "text-[#FF6600]" : "text-[#A3A3A3]"
                        }`}
                      >
                        {active ? "Selected" : "Select Platform"}{" "}
                        {active && <Check className="w-3.5 h-3.5" />}
                      </div>
                    </button>
                  );
                })}
              </div>

              {/* Compatibility guide link + WhatsApp link — small helpers
                  beneath the cards. Both stay collapsed/optional per the spec. */}
              <div className="mt-5 flex flex-col sm:flex-row gap-3 sm:gap-6 text-xs">
                <button
                  data-testid="platform-compat-guide-link"
                  onClick={() => setCompatModalOpen(true)}
                  className="text-left text-[#FF6600] hover:underline font-semibold self-start"
                >
                  {cfg.compat_guide_link_label}
                </button>
                {cfg.whatsapp_link_label && cfg.whatsapp_url && (
                  <a
                    data-testid="platform-whatsapp-link"
                    href={cfg.whatsapp_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 text-[#A3A3A3] hover:text-white transition-colors"
                  >
                    <MessageCircle className="w-3.5 h-3.5" />
                    {cfg.whatsapp_link_label}
                  </a>
                )}
              </div>
            </div>
          )}

          {/* Step 2 — Choose Your Performance. Reads tiers from the currently
              selected platform. Rendered only after a platform is selected;
              otherwise a placeholder nudges the customer back to Step 1. */}
          {!noPlatformActive && (
            <div>
              <StepHeader n={2} title={cfg.step1_heading} sub={cfg.step1_sub} />
              {!currentPlatform ? (
                <div
                  data-testid="performance-locked-placeholder"
                  className="mt-8 ur-card p-6 text-sm text-[#A3A3A3]"
                >
                  Pick a platform above to see the available performance tiers.
                </div>
              ) : (
                <div className="mt-8 grid md:grid-cols-3 gap-5">
                  {tiers.map((t) => {
                    const active = t.id === tierId;
                    const label = displayTierName(t.name, cfg.essential_label);
                    const model = (t.current_model || "").trim();
                    return (
                      <button
                        key={t.id}
                        data-testid={`setup-tier-${label.toLowerCase()}`}
                        onClick={() => setTierId(t.id)}
                        className={`text-left ur-card p-6 transition-all ${
                          active ? "border-[#FF6600] ring-2 ring-[#FF6600]/30" : "hover:border-[#FF6600]/40"
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="font-display text-2xl font-bold">{label}</div>
                          {t.popular && (
                            <span className="text-[10px] font-bold uppercase tracking-widest bg-[#FF6600] text-black px-2 py-1 rounded-full flex items-center gap-1">
                              <Star className="w-3 h-3" /> Popular
                            </span>
                          )}
                        </div>
                        {t.description && <div className="text-xs text-[#A3A3A3] mt-2">{t.description}</div>}
                        <div className="mt-5 font-display text-3xl font-black">AWG {formatAWG(t.price)}</div>
                        <div className="text-xs text-[#A3A3A3] mt-1">{usdApprox(t.price)}</div>
                        {/* Current Model row — rendered only when populated.
                            When empty, the whole block is omitted (no
                            placeholder / "TBD" copy per the spec). */}
                        {model && (
                          <div
                            data-testid={`setup-tier-${label.toLowerCase()}-current-model`}
                            className="mt-4 rounded-lg border border-[#262626] bg-[#0F0F0F] px-3 py-2.5"
                          >
                            <div className="text-[10px] uppercase tracking-widest text-[#A3A3A3]">
                              {cfg.platform_current_model_label}
                            </div>
                            <div className="mt-0.5 font-semibold text-white text-sm leading-snug">
                              {model}
                            </div>
                            <div className="mt-1.5 text-[10px] text-[#A3A3A3] leading-snug">
                              {cfg.platform_current_model_disclaimer}
                            </div>
                          </div>
                        )}
                        {t.features?.length > 0 && (
                          <ul className="mt-5 space-y-2 text-sm">
                            {t.features.map((f) => (
                              <li key={f} className="flex items-start gap-2 text-[#D1D1D1]">
                                <Check className="w-3.5 h-3.5 text-[#FF6600] mt-1 shrink-0" />
                                {f}
                              </li>
                            ))}
                          </ul>
                        )}
                        <div
                          className={`mt-5 inline-flex items-center gap-2 text-xs font-bold ${
                            active ? "text-[#FF6600]" : "text-[#A3A3A3]"
                          }`}
                        >
                          {active ? "Selected" : "Select Tier"}{" "}
                          {active && <Check className="w-3.5 h-3.5" />}
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Step 3 — Extras (renumbered from old Step 2). Unchanged copy/UX. */}
          {setup.addons?.length > 0 && (
            <div>
              <StepHeader n={3} title={cfg.step2_heading} sub={cfg.step2_sub} />
              <div className="mt-8 grid sm:grid-cols-2 gap-3">
                {setup.addons.map((a) => {
                  const active = selectedAddons.has(a.id);
                  return (
                    <button
                      key={a.id}
                      data-testid={`setup-addon-${a.id}`}
                      onClick={() => toggleAddon(a.id)}
                      className={`text-left ur-card p-5 flex items-start gap-4 transition-all ${
                        active ? "border-[#FF6600] bg-[#1A1209]" : "hover:border-[#FF6600]/40"
                      }`}
                    >
                      <div
                        className={`w-5 h-5 rounded-md border grid place-items-center shrink-0 mt-0.5 ${
                          active ? "bg-[#FF6600] border-[#FF6600]" : "border-[#444]"
                        }`}
                      >
                        {active && <Check className="w-3.5 h-3.5 text-black" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-3">
                          <div className="font-semibold">{a.name}</div>
                          <div className="font-display font-bold text-[#FF6600] shrink-0">
                            +AWG {formatAWG(a.price)}
                          </div>
                        </div>
                        {a.description && (
                          <div className="text-xs text-[#A3A3A3] mt-1">{a.description}</div>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Step 4 — Contact & fulfillment (renumbered from old Step 3). */}
          <div>
            <StepHeader n={4} title={cfg.step3_heading} sub={cfg.step3_sub} />
            <div
              data-testid="setup-contact-form"
              className="mt-8 ur-card p-6 grid sm:grid-cols-2 gap-4"
            >
              <ContactField
                label="Name *"
                value={contact.name}
                onChange={(v) => setContact((c) => ({ ...c, name: v }))}
                testId="contact-name"
              />
              <ContactField
                label="Email *"
                type="email"
                value={contact.email}
                onChange={(v) => setContact((c) => ({ ...c, email: v }))}
                testId="contact-email"
              />
              <ContactField
                label="Phone / WhatsApp *"
                value={contact.phone}
                onChange={(v) => setContact((c) => ({ ...c, phone: v }))}
                testId="contact-phone"
              />
              <ContactField
                label="Business / Company"
                value={contact.business}
                onChange={(v) => setContact((c) => ({ ...c, business: v }))}
                testId="contact-business"
              />

              <div className="sm:col-span-2">
                <ContactField
                  label="Delivery Address *"
                  value={contact.delivery_address}
                  onChange={(v) => setContact((c) => ({ ...c, delivery_address: v }))}
                  testId="contact-address"
                  placeholder="Street, city, notes for finding you"
                />
                <p
                  data-testid="delivery-note"
                  className="mt-2 text-[11px] text-[#A3A3A3] leading-snug"
                >
                  {cfg.delivery_note}
                </p>
              </div>

              <div className="sm:col-span-2">
                <label className="block">
                  <span className="text-[10px] uppercase tracking-widest text-[#A3A3A3]">
                    Anything we should know?
                  </span>
                  <textarea
                    data-testid="contact-notes"
                    value={contact.notes}
                    onChange={(e) => setContact((c) => ({ ...c, notes: e.target.value }))}
                    rows={3}
                    placeholder="Timing, preferences, special requests (optional)"
                    className="mt-1 w-full bg-[#141414] border border-[#262626] rounded-lg px-3 py-2 text-sm text-white focus:border-[#FF6600] outline-none resize-y"
                  />
                </label>
              </div>
            </div>
          </div>
        </div>

        {/* Summary sidebar */}
        <aside data-testid="setup-summary" className="lg:sticky lg:top-28 ur-card p-6">
          <div className="ur-eyebrow">Order Summary</div>
          <h3 className="font-display mt-3 text-2xl font-bold">{setup.name}</h3>
          <div className="mt-6 space-y-3 text-sm">
            {currentPlatform && (
              <Row
                label="Platform"
                value={platformDisplayName(currentPlatform.key)}
              />
            )}
            {tier && (
              <Row
                label={`Performance · ${displayTierName(tier.name, cfg.essential_label)}`}
                value={`AWG ${formatAWG(tier.price)}`}
              />
            )}
            {!currentPlatform && (
              <Row label="Platform" value="Not selected" muted />
            )}
            {addonItems.map((a) => (
              <Row key={a.id} label={a.name} value={`+AWG ${formatAWG(a.price)}`} muted />
            ))}
          </div>
          <div className="mt-6 pt-6 border-t border-[#262626] space-y-2 text-sm">
            <div className="flex items-center justify-between">
              <span className="text-[#A3A3A3]">Subtotal</span>
              <span data-testid="setup-subtotal" className="font-semibold text-white tabular-nums">AWG {formatAWG(total)}</span>
            </div>
            {taxRate > 0 && (
              <div className="flex items-center justify-between">
                <span className="text-[#A3A3A3]" data-testid="setup-tax-label">{taxLabel}</span>
                <span data-testid="setup-tax-amount" className="font-semibold text-white tabular-nums">AWG {formatAWG(taxAmount)}</span>
              </div>
            )}
            <div className="pt-2 border-t border-[#262626] flex items-end justify-between">
              <span className="text-white font-semibold">Estimated Total</span>
              <div className="text-right">
                <span data-testid="setup-total" className="font-display text-3xl font-black block leading-none">
                  AWG {formatAWG(totalWithTax)}
                </span>
                <span className="text-xs text-[#A3A3A3] mt-1 inline-block">{usdApprox(totalWithTax)}</span>
              </div>
            </div>
          </div>
          <button
            data-testid="setup-submit"
            onClick={handleSubmit}
            disabled={!canSubmit}
            className="ur-btn-primary w-full mt-6 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitting ? "Submitting…" : cfg.submit_label}{" "}
            <ArrowRight className="w-4 h-4" />
          </button>
          {!contactValid && (
            <div className="mt-3 text-[11px] text-[#F59E0B]">
              Complete all required steps — Platform, Performance, and your details (Name, Email, Phone, Delivery Address).
            </div>
          )}
          <div className="mt-5 flex items-start gap-2 text-xs text-[#A3A3A3]">
            <Sparkles className="w-3.5 h-3.5 text-[#FF6600] mt-0.5 shrink-0" />
            {cfg.sentoo}
          </div>
        </aside>
      </section>

      {/* Compatibility Guide Modal — shared component used by the Step 1
          "Not sure?" link. Content comes from the per-workstation
          `compatibility_guide` field on the laptop doc, with a global empty-
          state fallback so a fresh workstation doesn't look broken. */}
      {compatModalOpen && (
        <CompatGuideModal
          title={cfg.compat_guide_modal_title}
          content={setup.compatibility_guide || cfg.compat_guide_empty_state}
          onClose={() => setCompatModalOpen(false)}
        />
      )}
    </div>
  );
}

const StepHeader = ({ n, title, sub }) => (
  <div className="flex items-center gap-4">
    <div className="w-11 h-11 rounded-full bg-[#FF6600] text-black font-display font-black grid place-items-center">
      {n}
    </div>
    <div>
      <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight">{title}</h2>
      <p className="text-sm text-[#A3A3A3] mt-1">{sub}</p>
    </div>
  </div>
);

const Row = ({ label, value, muted }) => (
  <div className="flex items-start justify-between gap-4">
    <span className={muted ? "text-[#A3A3A3]" : "text-white"}>{label}</span>
    <span className={`font-semibold ${muted ? "text-[#D1D1D1]" : "text-white"}`}>{value}</span>
  </div>
);

const ContactField = ({ label, value, onChange, type = "text", placeholder, testId }) => (
  <label className="block">
    <span className="text-[10px] uppercase tracking-widest text-[#A3A3A3]">{label}</span>
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      data-testid={testId}
      className="mt-1 w-full bg-[#141414] border border-[#262626] rounded-lg px-3 py-2 text-sm text-white focus:border-[#FF6600] outline-none"
    />
  </label>
);

// Modal is intentionally kept in this file — it's a page-scoped helper and
// gets its data (title/content) from the caller, so promoting it to a shared
// component would just add indirection with zero reuse today.
const CompatGuideModal = ({ title, content, onClose }) => {
  useEffect(() => {
    const onEsc = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onEsc);
    return () => document.removeEventListener("keydown", onEsc);
  }, [onClose]);

  return (
    <div
      data-testid="platform-compat-guide-modal"
      className="fixed inset-0 z-[110] bg-black/70 backdrop-blur-sm grid place-items-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-[#141414] border border-[#262626] rounded-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#262626]">
          <div>
            <div className="ur-eyebrow">Compatibility</div>
            <h3 className="font-display text-xl font-bold mt-1">{title}</h3>
          </div>
          <button
            data-testid="platform-compat-guide-modal-close"
            onClick={onClose}
            className="w-9 h-9 rounded-full border border-[#262626] grid place-items-center hover:border-[#FF6600] transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="px-6 py-6 overflow-y-auto text-sm text-[#D1D1D1] leading-relaxed whitespace-pre-line">
          {content}
        </div>
      </div>
    </div>
  );
};
